import {
  SubmissionStatus,
  ReviewMode,
  SignatureType,
  ReviewerStatus,
  SignatureStatus,
} from "encamp-prisma/generated/browser";

import { isStaffUserType } from "./staffDisplay";

/**
 * Structural type representing a submission reviewer with just the fields
 * needed to compute who we're waiting on. This is intentionally loose to
 * accept any object with at least these properties, and is generic over
 * the User type to preserve the actual type through the function.
 */
export type SubmissionReviewerForWaitingOn<TUser> = {
  isSigner: boolean;
  reviewOrder: number;
  user: TUser;
  completedAt: Date | null;
  reviewerStatus: ReviewerStatus | null;
  signatureStatus?: SignatureStatus;
};

export type SubmissionForWaitingOn<TUser> = {
  status: SubmissionStatus;
  reviewMode: ReviewMode;
  signatureType: SignatureType | null;
  reviewers: SubmissionReviewerForWaitingOn<TUser>[];
};

/**
 * Statuses where a submission can be "waiting on" reviewers.
 * - ENCAMP_INITIAL_REVIEW: staff reviewer must complete before sending to external reviewers
 * - IN_REVIEW: external reviewers (and signers) are actively reviewing
 */
export const REVIEW_STATUSES: SubmissionStatus[] = [
  SubmissionStatus.ENCAMP_INITIAL_REVIEW,
  SubmissionStatus.IN_REVIEW,
];

export function computeWaitingOnUsers<TUser extends { type: string }>(
  submission: SubmissionForWaitingOn<TUser>
): TUser[] {
  return computeWaitingOnReviewers(submission).map((r) => r.user);
}

/** DIGITAL and WET_INK signers wait for every non-signer to approve. */
export function requiresSignerGating(signatureType: SignatureType | null): boolean {
  return signatureType === SignatureType.DIGITAL || signatureType === SignatureType.WET_INK;
}

/** The reviewer rows behind {@link computeWaitingOnUsers}. */
export function computeWaitingOnReviewers<
  TUser extends { type: string },
  TReviewer extends SubmissionReviewerForWaitingOn<TUser> = SubmissionReviewerForWaitingOn<TUser>,
>(
  submission: Omit<SubmissionForWaitingOn<TUser>, "reviewers"> & { reviewers: TReviewer[] }
): TReviewer[] {
  if (!REVIEW_STATUSES.includes(submission.status)) {
    return [];
  }

  const reviewers: TReviewer[] =
    submission.status === SubmissionStatus.ENCAMP_INITIAL_REVIEW
      ? submission.reviewers.filter((reviewer) => isStaffUserType(reviewer.user.type))
      : submission.reviewers;

  if (reviewers.length === 0) {
    return [];
  }

  const signerGating = requiresSignerGating(submission.signatureType);

  const eligibleNonSignersApproved = !signerGating
    ? true
    : reviewers
        .filter((r) => !r.isSigner)
        .every((r) => r.reviewerStatus === ReviewerStatus.APPROVED);

  const pendingReviewers = reviewers.filter((r) => {
    const isPending =
      r.completedAt == null &&
      r.reviewerStatus == null &&
      r.signatureStatus !== SignatureStatus.DECLINED &&
      r.signatureStatus !== SignatureStatus.REVOKED &&
      r.signatureStatus !== SignatureStatus.EXPIRED;

    if (!isPending) {
      return false;
    }

    // Preparing a digital request does not make it actionable until it is sent.
    if (
      r.isSigner &&
      submission.signatureType === SignatureType.DIGITAL &&
      r.signatureStatus !== SignatureStatus.SENT
    ) {
      return false;
    }

    // For DIGITAL and WET_INK signatures: do not surface signers as "waiting on"
    // until all eligible non-signers have approved.
    if (signerGating && r.isSigner && !eligibleNonSignersApproved) {
      return false;
    }

    return true;
  });

  const pendingSorted = pendingReviewers.sort((a, b) => a.reviewOrder - b.reviewOrder);

  // ENCAMP_INITIAL_REVIEW: only the first pending staff reviewer
  // External reviewers haven't been notified yet, so they shouldn't appear as "waiting on"
  if (submission.status === SubmissionStatus.ENCAMP_INITIAL_REVIEW) {
    return pendingSorted.slice(0, 1);
  }

  // IN_REVIEW: respect the submission's reviewMode
  return submission.reviewMode === ReviewMode.SEQUENTIAL
    ? pendingSorted.slice(0, 1)
    : pendingSorted;
}
